import * as XLSX from "xlsx";
import TimeFormatter from "../extensions/TimeFormatter";
import {
  getDeveloperTimeBreakdown,
  calculateBudgetaryPerformance,
  getDeveloperTimeMap,
} from "./taskReportsSlice";
import { MilestoneTask } from "./MilestoneTask";

export class MilestoneTaskReportExcelExporter {
  exportToExcel(milestoneTasks: MilestoneTask[], fileName: string) {
    const fileNameWithExtension = fileName.endsWith(".xlsx")
      ? fileName
      : `${fileName}.xlsx`;

    const maxDevelopers = milestoneTasks.reduce((max, task) => {
      const developerTimeMap = getDeveloperTimeMap(task);
      return Math.max(max, developerTimeMap.size);
    }, 0);

    const baseHeaders = [
      "Task Number",
      "Task Title",
      "Developer(s)",
      "Total Time",
      "Time Budget",
      "Budgetary Performance",
      "Date Range",
      "Date Start",
      "Date End",
      "Decimal Total Time",
      "Decimal Time Budget",
      "Time Split",
    ];

    const developerHeaders = Array.from(
      { length: Math.min(maxDevelopers, 3) },
      (_, i) => `Developer ${i + 1}`
    );
    const headers = [...baseHeaders, ...developerHeaders];

    const timeFormatter = new TimeFormatter();

    const sortedMilestoneTasks = [...milestoneTasks].sort((a, b) => {
      const dateEndA = Math.max(
        ...a.taskReports.map((report) =>
          new Date(report.dateEnd).getTime()
        )
      );
      const dateEndB = Math.max(
        ...b.taskReports.map((report) =>
          new Date(report.dateEnd).getTime()
        )
      );

      return dateEndB - dateEndA;
    });

    const data = sortedMilestoneTasks.map((task) => {
      const totalTimeSpent = task.taskReports.reduce(
        (sum, report) => sum + report.totalTimeSpentOnTask,
        0
      );
      const budgetaryPerformance = calculateBudgetaryPerformance(task);
      const dateStart = task.taskReports.reduce((earliest, report) => {
        const reportDate = new Date(report.dateStart).getTime();
        return reportDate < earliest ? reportDate : earliest;
      }, Infinity);
      const dateEnd = task.taskReports.reduce((latest, report) => {
        const reportDate = new Date(report.dateEnd).getTime();
        return reportDate > latest ? reportDate : latest;
      }, 0);
      const developerTimeBreakdown = getDeveloperTimeBreakdown(task);
      const developerNames = developerTimeBreakdown
        .split("\n")
        .map((entry) => entry.split(": ")[0])
        .join(", ");

      const developerTimeMap = getDeveloperTimeMap(task);
      const developerDetails = Array.from(developerTimeMap.entries())
        .sort(([, timeA], [, timeB]) => timeB - timeA)
        .map(([developerName, time]) => {
          return { name: developerName, time };
        });

      const rowData: Record<string, any> = {
        "Task Number": task.gitLabTaskNumber.toString(),
        "Task Title": task.taskTitle,
        "Developer(s)": developerNames,
        "Total Time": timeFormatter.toHoursAndMinutes(totalTimeSpent),
        "Time Budget":
          typeof task.timeEstimate === "number"
            ? timeFormatter.toHoursAndMinutes(task.timeEstimate)
            : "N/A",
        "Budgetary Performance":
          budgetaryPerformance === 0
            ? 0
            : Number(budgetaryPerformance.toFixed(2)),
        "Date Range": `${new Date(dateStart).toLocaleDateString()} - ${new Date(
          dateEnd
        ).toLocaleDateString()}`,
        "Date Start": new Date(dateStart).toLocaleDateString(),
        "Date End": new Date(dateEnd).toLocaleDateString(),
        "Decimal Total Time":
          typeof totalTimeSpent === "number"
            ? Number(totalTimeSpent.toFixed(2))
            : 0,
        "Decimal Time Budget":
          typeof task.timeEstimate === "number"
            ? Number(task.timeEstimate.toFixed(2))
            : "N/A",
        "Time Split": getDeveloperTimeBreakdown(task, true),
      };

      developerDetails.slice(0, 3).forEach((dev, index) => {
        rowData[`Developer ${index + 1}`] = `${
          dev.name
        }: ${timeFormatter.toHoursAndMinutes(dev.time)}`;
      });

      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });

    const baseColWidths = [
      { wch: 85 / 7 }, // Task Number
      { wch: 350 / 7 }, // Task Title
      { wch: 180 / 7 }, // Developer(s)
      { wch: 90 / 7 }, // Total Time
      { wch: 90 / 7 }, // Time Budget
      { wch: 150 / 7 }, // Budgetary Performance
      { wch: 145 / 7 }, // Date Range
      { wch: 90 / 7 }, // Date Start
      { wch: 90 / 7 }, // Date End
      { wch: 100 / 7 }, // Decimal Total Time
      { wch: 120 / 7 }, // Decimal Time Budget
      { wch: 380 / 7 }, // Time Split
    ];

    const developerColWidths = Array.from({ length: maxDevelopers }, () => ({
      wch: 180 / 7,
    }));
    worksheet["!cols"] = [...baseColWidths, ...developerColWidths];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TaskReports");
    XLSX.writeFile(workbook, fileNameWithExtension);
  }
}
